import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Rating } from '@sunwebgroup/uds-ui/rating'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { values } from 'ramda'
import * as React from 'react'
import { useState } from 'react'
import { useIntl } from '@/intl'
import { ServiceIcon } from '@/scopes/checkout/components/ServiceIcon'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import styles from './BasketLegacy.module.css'

export function BasketLegacy() {
  return (
    <>
      <div className={styles.checkoutSection}>
        <DesktopBasket />
      </div>
      <MobileBasket />
    </>
  )
}

function DesktopBasket() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  if (isLoading)
    return ''
  const basketInformation = data?.legacyBasketInformation
  const { groupInformation } = data ?? {}
  const { participants } = data ?? {}
  if (basketInformation && !basketInformation.accommodationName)
    return ''

  const startDate = basketInformation?.accommodationDates?.startDate ? new Date(basketInformation.accommodationDates.startDate) : null
  const endDate = basketInformation?.accommodationDates?.endDate ? new Date(basketInformation.accommodationDates.endDate) : null

  return (
    <>
      <div className={styles.basketImageWrapper}>
        <MediaServerLazyImage
          src={basketInformation?.imageUrl ?? ''}
          widths={[560, 940, 1200]}
          alt={basketInformation?.accommodationName}
          width={560}
          height={300}
          loading="lazy"
        />
      </div>
      <div className={styles.content}>
        <div className={styles.flexColumn}>
          <Rating rating={basketInformation?.rating || 0} />
          <Typography className={styles.accommodationName} variant="display-h-regular">
            {basketInformation?.accommodationName}
          </Typography>
          <Typography className={styles.location} variant="body-s-regular-tight">
            {basketInformation?.location}
          </Typography>
        </div>
        <div className={styles.flexColumn}>
          {!groupInformation && <IncludedServices includedServices={basketInformation?.servicesIncluded ?? []} />}
          {groupInformation && basketInformation?.accommodationDates && (
            <>
              <Typography className={styles.departureDate}>
                {startDate?.toLocaleDateString(intl.locale, { weekday: 'short', day: 'numeric', month: 'short' })}
                {' - '}
                {endDate?.toLocaleDateString(intl.locale, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
                {' '}
                (
                {basketInformation?.accommodationDates?.duration}
                )
              </Typography>
            </>
          )}
          {(!groupInformation || !basketInformation?.accommodationDates) && (
            <>
              <Typography className={styles.departureDate}>{basketInformation?.departureDate}</Typography>
              <Typography className={styles.bookingDetails}>
                {basketInformation?.duration}
                {participants && values(participants).length > 1 && `, ${basketInformation?.occupancy}`}
              </Typography>
            </>
          )}
        </div>

        {groupInformation && (
          <div className={styles.flexColumn}>
            <Typography className={styles.groupDetailsLabel}>
              {intl.formatMessage({ id: 'groups_checkout_steps_basket_group_info_label' })}
            </Typography>
            <Typography className={styles.groupDetailsName}>
              {intl.formatMessage(
                { id: 'groups_checkout_steps_basket_group_information' },
                { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
              )}
            </Typography>
          </div>
        )}
        <PriceBreakDown />
        <ServicesOverview servicesOverview={basketInformation?.servicesOverview} />
      </div>
    </>
  )
}

function BasketMobileTotalBottom({
  ref,
  totalPrice,
  isOpen,
  ...props
}: {
  ref?: React.Ref<HTMLDivElement>
  totalPrice?: string
  isOpen: boolean
}) {
  const intl = useIntl()

  const ToggleBanner = isOpen ? Sheet.Close : Sheet.Trigger

  return (
    <ToggleBanner>
      <div ref={ref} {...props} role="button" className={cn(styles.mobileTotalBottom, isOpen && styles.mobileTotalBottomOnTop)}>
        <Typography variant="body-m-bold-tight">{intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}</Typography>
        <div className={styles.mobileTotalBottomPrice}>
          <Typography variant="body-m-bold-tight">
            {totalPrice}
          </Typography>
          {isOpen ? <Icon glyph="ChevronDown" size={24} /> : <Icon glyph="ChevronUp" size={24} />}
        </div>
      </div>
    </ToggleBanner>
  )
}

function MobileBasket() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  const [isOpen, setIsOpen] = useState(false)

  if (isLoading)
    return ''
  const basketInformation = data?.legacyBasketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''
  const { groupInformation } = data ?? {}

  const toggle = (value: boolean) => {
    setIsOpen(value)
  }

  return (
    <Sheet.Root onOpenChange={toggle}>
      <BasketMobileTotalBottom totalPrice={basketInformation?.totalPrice} isOpen={isOpen} />
      <Sheet.Body side="bottom">
        <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={intl.formatMessage({ id: 'checkout_steps_basket_mobile_header' })} />
        <Sheet.Content>
          <div className={styles.mobileContent}>
            <div className={styles.mobileHeader}>
              <div className={styles.basketImageWrapperMobile}>
                <MediaServerLazyImage src={basketInformation?.imageUrl ?? ''} widths={[560, 940, 1200]} alt={basketInformation?.accommodationName} width={560} height={300} loading="lazy" />
              </div>
              <div className={styles.flexColumn}>
                <Rating rating={basketInformation?.rating || 0} />
                <Typography className={styles.accommodationName}>
                  {basketInformation?.accommodationName}
                </Typography>
                <Typography className={styles.location}>{basketInformation?.location}</Typography>
              </div>
            </div>
            <div className={styles.flexColumn}>
              <Typography className={styles.departureDate}>{basketInformation?.departureDate}</Typography>
              <Typography className={styles.bookingDetails}>
                {basketInformation?.duration}
                {', '}
                {basketInformation?.occupancy}
              </Typography>
            </div>
            {groupInformation && (
              <div className={styles.flexColumn}>
                <Typography className={styles.groupDetailsLabel}>
                  {intl.formatMessage({ id: 'groups_checkout_steps_basket_group_info_label' })}
                </Typography>
                <Typography className={styles.groupDetailsName}>
                  {intl.formatMessage(
                    { id: 'groups_checkout_steps_basket_group_information' },
                    { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
                  )}
                </Typography>
              </div>
            )}
            <PriceBreakDown />
            <ServicesOverview servicesOverview={basketInformation?.servicesOverview} />
          </div>
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function ServicesOverview({ servicesOverview }: { servicesOverview?: Array<{ serviceAName?: string, icon: string, title: string, lines: Array<{ text: string, isTitle: boolean }> }> }) {
  if (!servicesOverview)
    return null

  return (
    <>
      {servicesOverview.map((service, index) => (
        <div key={index} className={styles.serviceOverview}>
          <div className={styles.serviceOverviewHeader}>
            <ServiceIcon type={service.serviceAName || service.icon} size={16} />
            <Typography variant="body-m-bold-tight" className={styles.serviceOverviewItemText}>{service.title}</Typography>
          </div>
          <div className={styles.serviceOverviewBody}>
            {service.lines.map((line, indexLine) => (
              <Typography key={indexLine} variant={line.isTitle ? 'body-m-bold-tight' : 'body-m-regular-tight'} className={cn(styles.serviceOverviewSubItemText, line.isTitle && styles.serviceOverviewItemTextTitle)}>
                {line.text}
              </Typography>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

function PriceBreakDown() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  if (isLoading)
    return ''
  const basketInformation = data?.legacyBasketInformation
  const { participants } = data ?? {}
  if (basketInformation && !basketInformation.accommodationName)
    return ''
  return (
    <>
      {data?.isRebooking
        ? (
            <div className={styles.flexColumn}>
              <Typography variant="body-m-bold-tight">
                {intl.formatMessage({ id: 'checkout_steps_basket_rebooking_price_breakdown' })}
              </Typography>
              <div className={styles.bookingPriceDetails}>
                <Typography variant="body-m-regular-tight" className={styles.bookingPriceDetailsLabel}>
                  {intl.formatMessage({ id: 'checkout_steps_basket_package_price_text' })}
                </Typography>
                <div className={styles.groupsBasketPrice}>
                  <Typography variant="body-m-bold-tight" className={styles.bookingPriceDetailsLabel}>
                    {basketInformation?.totalPackagePrice}
                  </Typography>
                  <Typography variant="body-s-regular-tight" className={styles.bookingPriceDetailsLabel}>
                    {basketInformation?.avgTotalPackagePrice}
                  </Typography>
                </div>
              </div>
              <div className={styles.bookingPriceDetails}>
                <Typography variant="body-m-regular-tight" className={styles.bookingPriceDetailsLabel}>
                  {intl.formatMessage({ id: 'checkout_steps_basket_previous_booking_paid_text' })}
                </Typography>
                <Typography variant="body-m-bold-tight" className={styles.bookingPriceDetailsLabel}>
                  {basketInformation?.originalBookingPaidAmount}
                </Typography>
              </div>
              <div className={styles.bookingPriceDetails}>
                <Typography variant="display-g-regular" className={styles.bookingPriceDetailsLabel}>
                  {intl.formatMessage({ id: 'checkout_steps_basket_amount_due_text' })}
                </Typography>
                <Typography variant="display-g-regular" className={styles.bookingPriceDetailsLabel}>
                  {basketInformation?.totalPrice}
                </Typography>
              </div>
              {basketInformation?.rebookingWarning && (
                <div className={styles.rebookingBanner}>
                  <Banner size="M" color="success">
                    <Typography variant="body-m-regular-tight">{basketInformation?.rebookingWarning}</Typography>
                  </Banner>
                </div>
              )}
            </div>
          )
        : (
            <div className={styles.flexColumn}>
              <div className={styles.bookingPriceDetails}>
                <Typography variant="display-g-regular" className={styles.bookingPriceDetailsLabel}>
                  {intl.formatMessage({ id: 'checkout_steps_basket_total_price_text' })}
                </Typography>
                <Typography variant="display-g-regular" className={styles.bookingPriceDetailsLabel}>
                  {basketInformation?.totalPrice}
                </Typography>
              </div>
              {participants && values(participants).length > 1 && (
                <div className={styles.bookingPriceDetails}>
                  <Typography variant="body-m-regular-tight" className={styles.bookingPriceDetailsLabel}>
                    {intl.formatMessage({ id: 'checkout_steps_basket_average_price_text' })}
                  </Typography>
                  <Typography variant="body-m-bold-tight" className={styles.bookingPriceDetailsLabel}>
                    {basketInformation?.avgPrice}
                  </Typography>
                </div>
              )}
            </div>
          )}
    </>
  )
}

function IncludedServices({ includedServices }: { includedServices: string[] }) {
  if (!includedServices?.length)
    return null

  return (
    <div className={styles.includedServicesWrapper}>
      {includedServices.map((service, index) => {
        return (
          <React.Fragment key={index}>
            <ServiceIcon type={service} size={24} className={styles.includedServiceIcon} />
            <Icon
              glyph="PlusExtraSmall"
              size={24}
              className={cn(styles.serviceIconDivider, styles.includedServiceIcon)}
            />
          </React.Fragment>
        )
      })}
    </div>
  )
}
